import React, { useEffect, useState } from 'react'
import {
  Select,
  Text,
  Label,
  XStack,
  View,
  useMedia,
  ScrollView,
  Dialog,
} from 'tamagui'
import { HeroIcon } from '@components/HeroIcon'
import { Modal } from './Modal'

interface IHeroSelectProps extends React.ComponentProps<typeof Select> {
  label?: string
  options: { name: string }[]
  showInvalidFeedback?: boolean
  placeholder?: string
}

export function HeroSelect({
  label,
  options,
  showInvalidFeedback = false,
  onValueChange,
  placeholder,
  ...props
}: IHeroSelectProps) {
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState(props.value)
  const [positionTop, setPositionTop] = useState(0)
  const media = useMedia()

  const handleValueChange = (value: string) => {
    setValue(value)
    onValueChange && onValueChange(value)
    setOpen(false)
  }

  useEffect(() => {
    setValue((prev) => {
      if (prev !== props.value) {
        setOpen(false)
        return props.value
      }
      return prev
    })
  }, [value, props])

  return (
    <>
      {label && (
        <Label
          color="$neutral-800"
          ff="$mulishRegular"
          fontSize="$font-size-xxs"
        >
          {label}
        </Label>
      )}
      {media.gtSm ? (
        <View gap={15} testID="desktop-select">
          <Select {...props} onValueChange={onValueChange}>
            <Select.Trigger
              width="100%"
              backgroundColor="$neutral-0"
              borderWidth={1}
              borderColor="$neutral-300"
              borderRadius={4}
              paddingVertical={8}
              paddingHorizontal={16}
              height={40}
              iconAfter={
                <HeroIcon
                  icon={['fal', 'chevron-down']}
                  size={16}
                  color="$neutral-300"
                />
              }
              onMouseEnter={(e) => {
                const position = e.nativeEvent.clientY
                setPositionTop(position)
              }}
            >
              <Select.Value
                placeholder={placeholder || ''}
                color="$neutral-600"
                numberOfLines={1}
              />
            </Select.Trigger>
            <Select.Content zIndex={200000}>
              <Select.Viewport
                maxWidth={150}
                style={{
                  top: positionTop,
                }}
                $gtSm={{ maxWidth: 'auto' }}
              >
                <Select.Group>
                  {options.map((option, i) => {
                    return (
                      <Select.Item
                        index={i}
                        key={option.name}
                        value={option.name.toLowerCase()}
                        hoverStyle={{ backgroundColor: '$primary-400' }}
                        backgroundColor={
                          props.value === option.name.toLocaleLowerCase()
                            ? '$neutral-0'
                            : '$neutral-100'
                        }
                      >
                        <Select.ItemText
                          color="$neutral-600"
                          whiteSpace="break-spaces"
                          paddingVertical={4}
                        >
                          {option.name}
                        </Select.ItemText>
                      </Select.Item>
                    )
                  })}
                </Select.Group>
              </Select.Viewport>
            </Select.Content>
          </Select>
        </View>
      ) : (
        <>
          <XStack
            testID="mobile-select"
            width="100%"
            backgroundColor="$neutral-0"
            borderWidth={1}
            borderColor="$neutral-300"
            borderRadius={4}
            paddingVertical={8}
            paddingHorizontal={16}
            height={40}
            justifyContent="space-between"
            alignItems="center"
            onPress={() => setOpen(true)}
          >
            <Text
              color="$neutral-800"
              ff="$mulishRegular"
              fontSize="$font-size-xxs"
              numberOfLines={1}
            >
              {props.value}
            </Text>
            <HeroIcon
              icon={['fal', 'chevron-down']}
              size={16}
              color="$neutral-300"
            />
            <Modal
              onClose={() => setOpen(false)}
              open={open}
              snapPoints={[80, 100]}
              backdropOpacity={0.9}
            >
              <Dialog.Title
                color="$neutral-800"
                ff="$mulishBold"
                fontSize="$font-size-md"
                textAlign="center"
                marginTop={20}
              >
                {label}
              </Dialog.Title>
              <ScrollView paddingVertical={20}>
                {options.map((option) => {
                  return (
                    <Text
                      key={option.name}
                      paddingHorizontal={14}
                      ff="$mulishRegular"
                      color="$neutral-600"
                      whiteSpace="break-spaces"
                      paddingVertical={8}
                      backgroundColor={
                        value === option.name ? '$primary-400' : '$neutral-0'
                      }
                      onPress={() => handleValueChange(option.name)}
                    >
                      {option.name}
                    </Text>
                  )
                })}
              </ScrollView>
            </Modal>
          </XStack>
        </>
      )}
      {showInvalidFeedback && (
        <XStack gap={5} alignItems="center" testID="select-invalid-feedback">
          <HeroIcon
            icon={['fal', 'exclamation-circle']}
            size={12}
            color="$danger-300"
          />
          <Text
            color="$danger-300"
            fontFamily="$mulishRegular"
            fontSize="$font-size-xxxs"
          >
            Este campo é obrigatório! Por favor, selecione uma opção.
          </Text>
        </XStack>
      )}
    </>
  )
}
